"use client";

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseScrollToBottomOptions {
  threshold?: number;
  dependencies?: React.DependencyList;
}

interface UseScrollToBottomReturn {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  endRef: React.RefObject<HTMLDivElement | null>;
  scrollToBottom: () => void;
  isNearBottom: boolean;
}

export function useScrollToBottom({
  threshold = 50,
  dependencies = []
}: UseScrollToBottomOptions = {}): UseScrollToBottomReturn {
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const isScrollingRef = useRef(false);

  // Check if user is near bottom
  const checkIfNearBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container || isScrollingRef.current) return false;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const nearBottom = distanceFromBottom <= threshold;
    
    setIsNearBottom(nearBottom);
    return nearBottom;
  }, [threshold]);

  // Scroll to bottom function - instant, no animations
  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    const endElement = endRef.current;
    
    if (!container || !endElement) return;

    isScrollingRef.current = true;
    
    // Simple instant scroll - no animations
    container.scrollTop = container.scrollHeight;
    
    // Reset scrolling flag after a short delay
    setTimeout(() => {
      isScrollingRef.current = false;
      checkIfNearBottom();
    }, 50);
  }, [checkIfNearBottom]);

  // Handle scroll events with debouncing
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      if (isScrollingRef.current) return;
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        checkIfNearBottom();
      }, 50);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [checkIfNearBottom]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (dependencies.length === 0) return;

    // Only scroll if user was near bottom
    if (isNearBottom && !isScrollingRef.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        scrollToBottom();
      }, 10);
    }
  }, [dependencies, isNearBottom, scrollToBottom]);

  // Handle viewport changes (simplified - no continuous listening)
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isNearBottom && !isScrollingRef.current) {
          scrollToBottom();
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [isNearBottom, scrollToBottom]);

  return {
    scrollRef,
    endRef,
    scrollToBottom,
    isNearBottom
  };
}
